import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Star, Globe, DollarSign, Zap, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const fadeUp = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 } };

const HERO_IMG = 'https://images.unsplash.com/photo-1581841064838-a470c740e8ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0JTIwc3R1ZGlvJTIwZGFyayUyMGJhY2tncm91bmQlMjBmYXNoaW9ufGVufDB8fHx8MTc4NTA3MTM1NHww&ixlib=rb-4.1.0&q=85&w=600';
const AGENCY_IMG = 'https://images.unsplash.com/photo-1518644730709-0835105d9daa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxidXNpbmVzc21hbiUyMGxhcHRvcCUyMG9mZmljZSUyMHByb2Zlc3Npb25hbCUyMHdvcmtpbmd8ZW58MHx8fHwxNzg1MDcxMzU0fDA&ixlib=rb-4.1.0&q=85&w=600';

const CREATOR_BENEFITS = [
  { icon: Users, title: 'Have your likeness work for you.', desc: 'Turn your likeness into a valuable asset.' },
  { icon: Star, title: 'Say yes to more opportunities.', desc: 'Accept more opportunities.' },
  { icon: Globe, title: 'Be anywhere in the world\u2014without leaving home.', desc: 'Collaborate globally from anywhere.' },
  { icon: DollarSign, title: 'Earn while you focus on what matters most.', desc: 'Create a new stream of income on your terms.' },
];

const AGENCY_BENEFITS = [
  { icon: Sparkles, title: 'Use AI to create licensed content with confidence.', desc: 'Legally cleared likenesses for AI-generated content.' },
  { icon: DollarSign, title: 'Reduce production costs without sacrificing quality.', desc: 'More content, less spend. Better ROI.' },
  { icon: Zap, title: 'Launch campaigns faster than ever before.', desc: 'Go from brief to final in days, not weeks.' },
  { icon: Globe, title: 'Work with talent from anywhere in the world.', desc: 'Access global talent and create without location limits.' },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="landing-page">
      {/* ═══ HEADER ═══ */}
      <header className="fixed top-0 w-full z-50 bg-[#0B0F19]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-[#6C3AED] flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className="text-white font-bold text-lg tracking-tight">MyLikeness</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Button onClick={() => navigate(user.role === 'creator' ? '/creator' : user.role === 'admin' ? '/admin' : '/agency')}
                className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white text-sm font-semibold h-10 px-5 rounded-full" data-testid="nav-dashboard-btn">
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-white/80 hover:text-white transition-colors hidden sm:block" data-testid="nav-login-btn">Log in</Link>
                <Button onClick={() => navigate('/signup')} className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white text-sm font-semibold h-10 px-6 rounded-full" data-testid="nav-get-started-btn">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section className="pt-24 pb-10 bg-[#0B0F19]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="text-center pt-10 pb-6">
            <p className="text-[#A78BFA] text-sm font-bold tracking-widest uppercase mb-4">MyLikeness</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="text-white">One likeness.</span><br />
              <span className="bg-gradient-to-r from-[#8B5CF6] via-[#6C3AED] to-[#A78BFA] bg-clip-text text-transparent">Infinite possibilities.</span>
            </h1>
            <p className="text-white/50 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              The likeness licensing platform empowering creators and agencies to create, collaborate and grow&#8202;&#8212;&#8202;together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ SPLIT SECTIONS ═══ */}
      <section className="relative" id="creators">
        <div className="flex flex-col lg:flex-row">

          {/* LEFT — FOR CREATORS */}
          <div className="lg:w-1/2 relative" style={{ background: 'linear-gradient(160deg, #0B0F19 0%, #1A102F 100%)' }}>
            {/* Center model image */}
            <div className="hidden lg:block absolute right-[-5%] top-0 bottom-0 w-[55%] z-0 overflow-hidden">
              <img src={HERO_IMG} alt="Creator" loading="lazy"
                className="w-full h-full object-cover object-center"
                style={{ maskImage: 'linear-gradient(to left, transparent 0%, black 40%)', WebkitMaskImage: 'linear-gradient(to left, transparent 0%, black 40%)' }} />
            </div>

            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.2 }}
              className="relative z-10 px-8 lg:px-12 xl:px-16 py-14 lg:py-20 max-w-lg">
              <p className="text-[#A78BFA] text-xs font-bold tracking-[0.2em] uppercase mb-5">For Creators</p>
              <h2 className="text-4xl sm:text-5xl font-bold text-white leading-[1.15] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Have your<br />likeness work<br /><em className="text-[#8B5CF6] italic">for you.</em>
              </h2>
              <p className="text-white/50 text-sm leading-relaxed mb-10 max-w-sm">
                You&apos;re more than one project. License your likeness on your terms and unlock opportunities that go beyond boundaries.
              </p>

              <div className="space-y-5 mb-10">
                {CREATOR_BENEFITS.map((b, i) => (
                  <div key={i} className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-[#6C3AED]/20 flex items-center justify-center flex-shrink-0">
                      <b.icon className="w-4.5 h-4.5 text-[#A78BFA]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold">{b.title}</p>
                      <p className="text-white/35 text-xs mt-0.5">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-5">
                <Button onClick={() => navigate('/creators')} data-testid="creator-cta-btn"
                  className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white h-12 px-7 rounded-full text-sm font-bold shadow-lg shadow-[#6C3AED]/30">
                  Learn more <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>

          {/* RIGHT — FOR AGENCIES */}
          <div className="lg:w-1/2 relative bg-white" id="agencies">
            {/* Businessman image */}
            <div className="hidden lg:block absolute right-0 top-0 w-[45%] h-full z-0 overflow-hidden">
              <img src={AGENCY_IMG} alt="Agency professional" loading="lazy"
                className="w-full h-full object-cover object-center opacity-90"
                style={{ maskImage: 'linear-gradient(to right, transparent 5%, black 50%)', WebkitMaskImage: 'linear-gradient(to right, transparent 5%, black 50%)' }} />
            </div>

            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.3 }}
              className="relative z-10 px-8 lg:px-12 xl:px-16 py-14 lg:py-20 max-w-lg">
              <p className="text-[#6C3AED] text-xs font-bold tracking-[0.2em] uppercase mb-5">For Agencies</p>
              <h2 className="text-4xl sm:text-5xl font-bold text-[#0B0F19] leading-[1.15] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                The right likeness.<br />For the <span className="text-[#6C3AED]">right result.</span>
              </h2>
              <p className="text-slate-500 text-sm leading-relaxed mb-10 max-w-sm">
                Find verified talent, secure rights, and create impactful campaigns&#8202;&#8212;&#8202;faster and easier.
              </p>

              <div className="space-y-5 mb-10">
                {AGENCY_BENEFITS.map((b, i) => (
                  <div key={i} className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-[#6C3AED]/10 flex items-center justify-center flex-shrink-0">
                      <b.icon className="w-4.5 h-4.5 text-[#6C3AED]" />
                    </div>
                    <div>
                      <p className="text-[#0B0F19] text-sm font-bold">{b.title}</p>
                      <p className="text-slate-400 text-xs mt-0.5">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-5">
                <Button onClick={() => navigate('/agencies')} data-testid="agency-cta-btn"
                  className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white h-12 px-7 rounded-full text-sm font-bold shadow-lg shadow-[#6C3AED]/30">
                  Learn more <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="bg-[#0B0F19] py-20 border-t border-white/5">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="text-center mb-14">
            <p className="text-[#A78BFA] text-xs font-bold tracking-[0.2em] uppercase mb-4">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Simple for creators.<br />Powerful for agencies.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Creator flow */}
            <div>
              <h3 className="text-white/80 text-xs font-bold tracking-[0.15em] uppercase mb-6">Creator Journey</h3>
              <div className="space-y-6">
                {[
                  { n: '01', t: 'Create your profile', d: 'Sign up, verify your identity, and set up your likeness portfolio.' },
                  { n: '02', t: 'Set your permissions', d: 'Define what you allow, what you ban, and how approvals work.' },
                  { n: '03', t: 'Review proposals', d: 'Agencies send proposals. Accept, negotiate, or decline on your terms.' },
                  { n: '04', t: 'Approve content & get paid', d: 'Review AI-generated content, approve it, and receive your payout via Stripe.' },
                ].map(s => (
                  <div key={s.n} className="flex items-start gap-4">
                    <span className="text-[#6C3AED] font-bold text-lg w-8 flex-shrink-0">{s.n}</span>
                    <div>
                      <p className="text-white font-semibold text-sm">{s.t}</p>
                      <p className="text-white/40 text-xs mt-0.5">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Agency flow */}
            <div>
              <h3 className="text-white/80 text-xs font-bold tracking-[0.15em] uppercase mb-6">Agency Journey</h3>
              <div className="space-y-6">
                {[
                  { n: '01', t: 'Set up your workspace', d: 'Create your agency, add payment details, and invite your team.' },
                  { n: '02', t: 'Build a campaign', d: 'Define the brief, channels, deliverables, and territory.' },
                  { n: '03', t: 'Send proposals to creators', d: 'Build licensing proposals with full terms, rights, and commercial offers.' },
                  { n: '04', t: 'Fund deals & generate content', d: 'Pay securely, upload AI-generated content, and manage approvals.' },
                ].map(s => (
                  <div key={s.n} className="flex items-start gap-4">
                    <span className="text-[#6C3AED] font-bold text-lg w-8 flex-shrink-0">{s.n}</span>
                    <div>
                      <p className="text-white font-semibold text-sm">{s.t}</p>
                      <p className="text-white/40 text-xs mt-0.5">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ABOUT US ═══ */}
      <section id="about" className="bg-white py-20">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-[#6C3AED] text-xs font-bold tracking-[0.2em] uppercase mb-4">About MyLikeness</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B0F19] leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Built for the future of<br /><span className="text-[#6C3AED]">creator licensing.</span>
            </h2>
            <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-8 max-w-2xl mx-auto">
              MyLikeness is the operating system for likeness licensing. We connect creators who want to monetise their identity with agencies who need verified, rights-cleared talent for AI-generated campaigns. Every deal is structured, every permission is explicit, and every payment is secure.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
              {[
                { value: 'Transparent', label: 'Every term is visible to both parties. No hidden clauses.' },
                { value: 'Secure', label: 'Stripe-powered payments, identity verification, and escrowed funds.' },
                { value: 'Creator-first', label: 'Creators control permissions, approvals, and content sign-off.' },
              ].map(v => (
                <div key={v.value} className="p-5 rounded-xl border border-slate-100">
                  <p className="text-[#0B0F19] font-bold text-base mb-1">{v.value}</p>
                  <p className="text-slate-400 text-xs leading-relaxed">{v.label}</p>
                </div>
              ))}
            </div>
            <Button onClick={() => navigate('/signup')} className="bg-[#6C3AED] hover:bg-[#5B2ED4] text-white h-12 px-8 rounded-full text-sm font-bold shadow-lg shadow-[#6C3AED]/20">
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="bg-[#0B0F19] py-12 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-[#6C3AED] flex items-center justify-center">
                <span className="text-white font-bold text-xs">M</span>
              </div>
              <span className="text-white/50 text-sm">MyLikeness &copy; {new Date().getFullYear()}</span>
            </div>
            <div className="flex items-center gap-6 flex-wrap justify-center md:justify-end">
              <Link to="/creators" className="text-white/30 hover:text-white/70 text-xs transition-colors">For Creators</Link>
              <Link to="/agencies" className="text-white/30 hover:text-white/70 text-xs transition-colors">For Agencies</Link>
              <Link to="/terms" className="text-white/30 hover:text-white/70 text-xs transition-colors">Terms</Link>
              <Link to="/privacy" className="text-white/30 hover:text-white/70 text-xs transition-colors">Privacy</Link>
              <Link to="/acceptable-use" className="text-white/30 hover:text-white/70 text-xs transition-colors">Acceptable Use</Link>
              <Link to="/login" className="text-white/30 hover:text-white/70 text-xs transition-colors">Sign In</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
